import React, { useMemo, useCallback, memo, useState } from 'react';
import { useSelector } from 'react-redux';
import { format } from 'date-fns-tz';
import { v4 as uuidv4 } from 'uuid';
import { contrastChart, Spinner, Icon } from '@iq/react-components';
import {
  ResponsiveContainer,
  CartesianGrid,
  AreaChart,
  Bar,
  Dot,
  LineChart,
  YAxis,
  XAxis,
  Line,
  BarChart,
  Tooltip,
  Legend,
  Area,
  ReferenceLine,
  ReferenceArea,
} from 'recharts';
import classnames from 'classnames';

import { getActiveSite } from '../../../../../bundles/sites';
import { useSubscription, useRefetchOnRangeChange } from '../../../../../bundles/visualizations';
import { getTimezone } from '../../../../../bundles/application';
import { getSiteVariablesIndex } from '../../../../../bundles/sources';
import { getNested, useClientSize } from '../../../../../utils';
import { utcToSite, getDateTickFormatter } from '../../../../../datetimeUtils';
import { THRESHOLD_TYPES } from '../../../../../constants';
import {
  convertValue,
  units,
  format as unitFormat,
  resolveVariableUnit,
  MULTIPLICATIVE_CATEGORIES,
} from '../../../../../units';
import { resolveThresholdItems } from '../../schemas/thresholdSchema';

import Loader from '../../../../Loader';
import { DropdownMenu } from '../../../../DropdownMenu';
import LabelWithTooltip from '../../../../LabelWithTooltip';
import { resolveColor } from '../../../../../colors';

const getColor = (index, config) => {
  if (config.color) {
    return resolveColor(config.color);
  }

  return contrastChart[index % contrastChart.length];
};

const Chart = ({ mode, ...properties }) => {
  switch (mode) {
    case 'area': {
      return <AreaChart {...properties} />;
    }
    case 'step': {
      return <LineChart {...properties} />;
    }
    case 'bar': {
      return <BarChart {...properties} />;
    }
    // Not being used currently
    // case 'line':
    default: {
      return <LineChart {...properties} />;
    }
  }
};

const ValueRepresentation = ({ mode, monotone, key, ...properties }) => {
  switch (mode) {
    case 'area': {
      return (
        <Area
          key={`${key}-${mode}`}
          type="monotone"
          {...properties}
        />
      );
    }
    case 'step': {
      return (
        <Line
          key={`${key}-${mode}`}
          type="stepBefore"
          {...properties}
        />
      );
    }
    case 'bar': {
      return (
        <Bar
          key={`${key}-${mode}`}
          barSize={2}
          {...properties}
        />
      );
    }
    // Not being used currently
    // case 'line':
    default: {
      return (
        <Line
          key={`${key}-${mode}`}
          type={monotone ? 'monotone' : 'linear'}
          {...properties}
        />
      );
    }
  }
};

const CustomLegend = ({
  thresholdsData,
  thresholdItems,
  seriesData,
  hiddenSeries,
  onToggleSeries,
}) => {
  return (
    <ul className="graph-legend">
      {seriesData.map((dataSet, index) => {
        const key = `${dataSet.color}-${index}`;
        const isHidden = hiddenSeries.has(dataSet.id);
        return (
          <li
            key={key}
            className={`graph-legend-item toggleable ${isHidden ? 'disabled' : ''}`}
            onClick={() => onToggleSeries(dataSet.id)}
          >
            <svg
              width="10"
              height="10"
            >
              <rect
                width="10"
                height="10"
                fill={resolveColor(dataSet.color)}
              />
            </svg>
            <span className={isHidden ? 'label-disabled' : ''}>
              {`${dataSet.label} (${dataSet.aggregate})`}
            </span>
          </li>
        );
      })}

      {thresholdItems.map((thd, index) => {
        const key = `${thd.fill} + ${index}`;
        return (
          <li
            key={key}
            className="graph-legend-item"
          >
            <svg
              width="40"
              height="5"
              className="graph-legend-image"
            >
              {thresholdsData.thresholdType === THRESHOLD_TYPES.linear.value && (
                <line
                  strokeDasharray="5, 3"
                  x1="0"
                  y1="0"
                  x2="40"
                  y2="0"
                  strokeWidth="5"
                  stroke={resolveColor(thd.fill)}
                />
              )}

              {thresholdsData.thresholdType === THRESHOLD_TYPES.area.value && (
                <rect
                  width="40"
                  height="5"
                  fill={resolveColor(thd.fill)}
                />
              )}
            </svg>
            {thd.description}
          </li>
        );
      })}
    </ul>
  );
};

/**
 * Gets the appropriate rounding interval based on the value's magnitude.
 * - < 10: round to nearest 5
 * - 10-999: round to nearest 10
 * - 1000-9999: round to nearest 100
 * - 10000-99999: round to nearest 1000
 * - and so on...
 * @param {number} value - The absolute value to determine interval for
 * @returns {number} The rounding interval
 */
const getRoundingInterval = (value) => {
  const absValue = Math.abs(value);

  if (absValue < 10) return 5;
  if (absValue < 1000) return 10;

  // For values >= 1000, use 1/10th of the magnitude
  const magnitude = Math.floor(Math.log10(absValue));
  return 10 ** (magnitude - 1);
};

/**
 * Calculates axis domain with 10% padding, rounded to nice intervals.
 * @param {number|null|undefined} configuredValue - User-configured min/max value
 * @param {number} dataMin - Minimum value from data
 * @param {number} dataMax - Maximum value from data
 * @param {boolean} isMin - Whether calculating min (true) or max (false)
 * @returns {number} The domain value
 */
const getNiceAxisDomain = (configuredValue, dataMin, dataMax, isMin) => {
  // Use configured value if provided
  if (configuredValue !== undefined && configuredValue !== null) {
    return configuredValue;
  }

  // Calculate 10% padding
  const range = dataMax - dataMin;
  const paddedValue = isMin ? dataMin - range * 0.1 : dataMax + range * 0.1;

  // Get the appropriate rounding interval based on the padded value
  const interval = getRoundingInterval(paddedValue);

  // Round down for min, up for max
  return isMin
    ? Math.floor(paddedValue / interval) * interval
    : Math.ceil(paddedValue / interval) * interval;
};

const GraphVisualization = memo(
  (GraphVisualizationProperties) => {
    const {
      visualization,
      panelId,
      pollingDateRange,
      onExportData,
      onFullscreen,
      isPreview,
      isFullscreen,
      syncVisualizationTooltip,
      selected,
      onSelect,
      openSignalViewer,
      getMoreOptions,
      dropdownPlacement,
      hideToolbar,
    } = GraphVisualizationProperties;
    const { duration = 0 } = pollingDateRange;
    const site = useSelector(getActiveSite);
    const timezone = useSelector(getTimezone);
    const variablesIndex = useSelector(getSiteVariablesIndex);

    const {
      initialLoaded,
      seriesValues: values,
      loading,
    } = useSubscription(visualization.id, panelId);
    const { variables = [], configuration = {} } = visualization;
    const {
      axes = {},
      series: variableAxisConfig = [],
      thresholds = {},
      legend: { show: showLegend } = {},
    } = configuration;
    const [{ width, height }, clientReference] = useClientSize();
    const [hiddenSeries, setHiddenSeries] = useState(new Set());

    useRefetchOnRangeChange({
      visualization,
      panelId,
      initialLoaded,
      selected,
      rangeKey: `${pollingDateRange.startDate}:${pollingDateRange.endDate}`,
    });

    const firstVariableDefaultUnit = variablesIndex?.[variables[0]?.id]?.unit || '';
    const lastVariableDefaultUnit = variablesIndex?.[variables.at(-1)?.id]?.unit || '';
    const data = useMemo(() => {
      if (!values) return [];
      const hasConfig = variables.some((variable) =>
        values.some((value) => value.variable === variable.id)
      );
      if (!hasConfig) return [];

      return values.reduce((acc, { values: dataValues, name, granularity }) => {
        const [id, agg] = name.split('.');
        const variableConfig = variables.find((v) => v.id === id && v.aggregate === agg);
        const variableUnit =
          variableConfig?.unit === 'variable-default'
            ? variablesIndex?.[id]?.unit || ''
            : variableConfig?.unit || '';

        const variableAxis = variableAxisConfig.find((s) => s.id === name)?.axis || 'left';
        const axisKey = `y${variableAxis.slice(0, 1).toUpperCase()}${variableAxis.slice(1)}`;
        const axisUnit =
          axes[axisKey]?.unit === 'variable-default' ? variableUnit : axes[axisKey]?.unit;

        for (const [time, value, quality] of dataValues) {
          const parsedValue = Number.parseFloat(value);
          const cleanedValue = Number.isNaN(parsedValue) ? undefined : parsedValue;

          let axisAlignedValue = cleanedValue;

          // we need to check that the variable unit is the same as the axis unit,
          // and normalize the value since we can receive values for the same var unit,
          // but differing prefixes (e.g. kW vs W)
          if (variableUnit !== axisUnit) {
            const { category: variableCat, multiplicative: variableMult } =
              units.find((u) => u.id === variableUnit) || {};
            const { category: axisCat, multiplicative: axisMult } =
              units.find((u) => u.id === axisUnit) || {};

            if (variableCat === axisCat && MULTIPLICATIVE_CATEGORIES.includes(variableCat)) {
              axisAlignedValue = convertValue(axisAlignedValue, variableMult, axisMult);
            }
          }
          if (quality !== 1) axisAlignedValue = null;

          const index = acc.findIndex((entry) => entry.time === time);
          if (index === -1) {
            const insertIndex = acc.findIndex(
              (_, index_) => acc[index_]?.time > time && acc[index_ - 1]?.time < time
            );
            if (insertIndex === -1) {
              acc.push({
                time,
                [name]: axisAlignedValue,
                granularity,
              });
            } else {
              acc = [
                ...acc.slice(0, insertIndex),
                {
                  time,
                  [name]: axisAlignedValue,
                  granularity,
                },
                ...acc.slice(insertIndex),
              ];
            }
          } else {
            acc[index] = {
              ...acc[index],
              [name]: axisAlignedValue,
            };
          }
        }
        return acc;
      }, []);
    }, [values, variables, variablesIndex, axes, variableAxisConfig, isPreview]);

    // Calculate data min/max for signal-based area thresholds
    const dataMinMax = useMemo(() => {
      if (data.length === 0) return { dataMin: 0, dataMax: 100 };

      let dataMin = Infinity;
      let dataMax = -Infinity;

      for (const entry of data) {
        for (const key of Object.keys(entry)) {
          if (key !== 'time' && key !== 'granularity') {
            const value = entry[key];
            if (typeof value === 'number' && !Number.isNaN(value)) {
              dataMin = Math.min(dataMin, value);
              dataMax = Math.max(dataMax, value);
            }
          }
        }
      }

      // Fallback if no valid values found
      if (dataMin === Infinity) dataMin = 0;
      if (dataMax === -Infinity) dataMax = 100;

      return { dataMin, dataMax };
    }, [data]);

    // Calculate sorted thresholds (supports both manual and signal-based)
    const sortedThresholds = useMemo(
      () => resolveThresholdItems(thresholds, variables, dataMinMax),
      [thresholds, variables, dataMinMax]
    );

    const CustomizedDot = useCallback(
      (customizedDotProperties) => {
        const { cx, cy, r, fill, index, dataKey, value } = customizedDotProperties;
        if (
          value !== undefined &&
          value !== null &&
          !data[index - 1]?.[dataKey] &&
          !data[index + 1]?.[dataKey]
        ) {
          return (
            <Dot
              cx={cx}
              cy={cy}
              r={r}
              fill={fill}
            />
          );
        }
        return null;
      },
      [data]
    );

    const ticks = useMemo(() => {
      const numTicks = Math.floor((width - 75) / 100);
      // data sets should be returning 100 datapoints
      const interval = Math.max(Math.floor(data.length / numTicks), 1);
      // Use offset start index by 1 interval so tick doesn't extend past y-axis
      const tickIndexes = [interval];
      while (tickIndexes.length <= numTicks) {
        tickIndexes.push(tickIndexes.at(-1) + interval);
      }
      return data.filter((_, index) => tickIndexes.includes(index)).map(({ time }) => time);
    }, [width, data]);

    const formatTicks = useCallback(
      getDateTickFormatter(duration, timezone, { ticks, configuration }),
      [duration, timezone, ticks, configuration]
    );

    const series = useMemo(
      () =>
        variables.map(({ id, aggregate, fill, label, unit, unitLabel, decimals }, index) => {
          const { name } = variablesIndex?.[id] || {};
          const seriesId = `${id}.${aggregate}`;
          const { series: seriesConfig = [] } = configuration;
          const config = seriesConfig.find(({ id: _id }) => _id === seriesId) || {};
          const { displayUnit, customUnit } = resolveVariableUnit(
            unit,
            unitLabel,
            variablesIndex?.[id]?.unit
          );

          return {
            variableId: id,
            id: seriesId,
            label: label || name,
            decimals: decimals || 'auto',
            unit: displayUnit,
            customUnit,
            aggregate,
            color: getColor(index, config),
            axis: config.axis || 'left',
            // linear and null-monotone fill options are deprecated, but still supported
            connectNulls: fill === 'connect' || fill === 'connect-monotone' || fill === 'linear',
            useCustomDots: fill === 'null' || fill === 'null-monotone',
            monotone: !!fill?.includes('monotone'),
          };
        }),
      [variables, variablesIndex, configuration]
    );

    const handleToggleSeries = useCallback(
      (seriesId) => {
        setHiddenSeries((prev) => {
          const newSet = new Set(prev);
          if (newSet.has(seriesId)) {
            newSet.delete(seriesId);
          } else if (series.length - newSet.size > 1) {
            newSet.add(seriesId);
          }
          return newSet;
        });
      },
      [series]
    );

    const getYaxisFormatter = (position, defaultUnit) => (v) => {
      const axisUnit = getNested(configuration, `axes.${position}.unit`);
      const { value, unit } = unitFormat(
        axisUnit === 'variable-default' ? defaultUnit : axisUnit,
        v,
        getNested(configuration, `axes.${position}.decimals`)
      );
      return `${value} ${unit}`;
    };

    const getYaxisLabel = (position) => ({
      value: getNested(configuration, `axes.${position}.label`),
      angle: -90,
      style: { textAnchor: 'middle' },
      position: position === 'yLeft' ? 'insideLeft' : 'insideRight',
    });

    const xScale = getNested(configuration, 'axes.x.scale') === 'linear' ? 'auto' : 'time';

    const tooltipLabelFormatter = (time) => {
      const dateFormat = 'yyyy-MM-dd, HH:mm:ss';
      return format(utcToSite(time, timezone), dateFormat, { timeZone: timezone });
    };

    const getValueFormatter =
      (decimals, variableUnit, aggregate, axis, customUnit) =>
      (value, _name, valueFormatterProperties) => {
        const { payload: { granularity } = {} } = valueFormatterProperties;
        const axisKey = `y${axis.slice(0, 1).toUpperCase()}${axis.slice(1)}`;
        const axisUnit =
          axes[axisKey]?.unit === 'variable-default'
            ? variableUnit
            : axes[axisKey]?.unit || variableUnit;
        let formatted = unitFormat(variableUnit, value, decimals, customUnit);
        if (variableUnit !== axisUnit) {
          const { category: variableCat } = units.find((u) => u.id === variableUnit) || {};
          const { category: axisCat } = units.find((u) => u.id === axisUnit) || {};

          if (variableCat === axisCat && MULTIPLICATIVE_CATEGORIES.includes(variableCat)) {
            formatted = unitFormat(axisUnit, value, decimals, customUnit);
          }
        }
        return `${formatted.value} ${formatted.unit} (${aggregate}, ${granularity})`;
      };

    const handleDownloadClick = () => {
      onExportData(data[0].granularity);
    };
    const handleSelect = () => {
      onSelect(visualization);
    };
    const classes = classnames('menu', {});

    const vizHeight = height - 28; // less header
    const loaderHeight = vizHeight - 28; // less loader padding

    const hasNumericData = data?.some((entry) =>
      Object.keys(entry).some(
        (key) =>
          key !== 'time' &&
          key !== 'granularity' &&
          typeof entry[key] === 'number' &&
          !Number.isNaN(entry[key])
      )
    );
    const isVizEmpty = !hasNumericData && !loading && initialLoaded;
    const showOptionsMenu = !isPreview && !isVizEmpty;

    return (
      <div
        className={`graph-visualization-component ${selected ? 'checked' : ''}`}
        ref={clientReference}
      >
        {!isFullscreen && (
          <div
            className="graph-header"
            onDoubleClick={(event) => {
              event.stopPropagation();
            }}
          >
            <div className="header-title truncateText">
              {!isPreview && !hideToolbar && (
                <div
                  className={`select ${selected ? 'checked' : ''}`}
                  onClick={handleSelect}
                >
                  <Icon
                    icon={`${selected ? 'he-checkbox-selected' : 'he-checkbox'}`}
                    size="s"
                  />
                </div>
              )}
              <div className="title truncateText">
                <LabelWithTooltip name={configuration?.title || visualization?.name || '\u00A0'} />
              </div>
            </div>
            {!isPreview && (
              <div className={classes}>
                {initialLoaded && loading && (
                  <Spinner
                    size="s"
                    className="spinner"
                  />
                )}
                {showOptionsMenu && (
                  <DropdownMenu
                    trigger="he-moreoptionsvertical"
                    triggerSize="s"
                    menuOptions={getMoreOptions({
                      site,
                      onFullscreen,
                      handleDownloadClick,
                      openSignalViewer,
                    })}
                    menuXPlacement={dropdownPlacement?.x}
                    menuYPlacement={dropdownPlacement?.y}
                    styles={{
                      dropdownIndicator: { padding: '0px' },
                      option: { cursor: 'pointer' },
                    }}
                  />
                )}
              </div>
            )}
          </div>
        )}
        {!initialLoaded && loading ? (
          <Loader
            text="Loading ..."
            height={loaderHeight}
          />
        ) : (
          <div className="graph-wrapper">
            <ResponsiveContainer
              width="100%"
              height={vizHeight}
            >
              <Chart
                syncId={syncVisualizationTooltip ? panelId : uuidv4()}
                mode={configuration.mode}
                data={data}
              >
                <CartesianGrid
                  strokeWidth={1}
                  strokeDasharray="3 3"
                />
                <YAxis
                  yAxisId="left"
                  domain={[
                    getNiceAxisDomain(
                      visualization.configuration?.axes?.yLeft?.minValue,
                      dataMinMax.dataMin,
                      dataMinMax.dataMax,
                      true
                    ),
                    getNiceAxisDomain(
                      visualization.configuration?.axes?.yLeft?.maxValue,
                      dataMinMax.dataMin,
                      dataMinMax.dataMax,
                      false
                    ),
                  ]}
                  axisLine={false}
                  tick={{ fontSize: 11 }}
                  tickFormatter={getYaxisFormatter('yLeft', firstVariableDefaultUnit)}
                  label={getYaxisLabel('yLeft')}
                  hide={getNested(configuration, 'axes.yLeft.show') === false}
                />
                <YAxis
                  yAxisId="right"
                  domain={[
                    getNiceAxisDomain(
                      visualization.configuration?.axes?.yRight?.minValue,
                      dataMinMax.dataMin,
                      dataMinMax.dataMax,
                      true
                    ),
                    getNiceAxisDomain(
                      visualization.configuration?.axes?.yRight?.maxValue,
                      dataMinMax.dataMin,
                      dataMinMax.dataMax,
                      false
                    ),
                  ]}
                  orientation="right"
                  axisLine={false}
                  tick={{ fontSize: 11 }}
                  tickFormatter={getYaxisFormatter('yRight', lastVariableDefaultUnit)}
                  label={getYaxisLabel('yRight')}
                  hide={getNested(configuration, 'axes.yRight.show') === false}
                />
                <XAxis
                  domain={['auto', 'auto']}
                  type="number"
                  scale={xScale}
                  axisLine={false}
                  dataKey="time"
                  tick={{ fontSize: 11 }}
                  ticks={ticks}
                  tickFormatter={formatTicks}
                  hide={getNested(configuration, 'axes.x.show') === false}
                />
                {series
                  .filter(({ id: seriesId }) => !hiddenSeries.has(seriesId))
                  .map(
                    ({
                      id: seriesId,
                      color,
                      label,
                      axis,
                      connectNulls,
                      decimals,
                      unit: baseUnit,
                      customUnit,
                      aggregate,
                      useCustomDots,
                      monotone,
                    }) =>
                      ValueRepresentation({
                        mode: configuration.mode || 'line',
                        data,
                        name: label,
                        key: seriesId,
                        dataKey: seriesId,
                        stroke: color,
                        fill: color,
                        yAxisId: axis,
                        animationDuration: 0,
                        isAnimationActive: false,
                        dot: useCustomDots && <CustomizedDot />,
                        strokeWidth: 2,
                        connectNulls,
                        monotone,
                        formatter: getValueFormatter(
                          decimals,
                          baseUnit,
                          aggregate,
                          axis,
                          customUnit
                        ),
                      })
                  )}

                <Tooltip
                  allowEscapeViewBox={{ x: false, y: true }}
                  isAnimationActive={false}
                  wrapperStyle={{ zIndex: '1000' }}
                  labelFormatter={tooltipLabelFormatter}
                  labelStyle={{ marginBottom: '0.4rem' }}
                />

                {sortedThresholds.map((threshold, index) => {
                  if (thresholds.thresholdType === THRESHOLD_TYPES.linear.value) {
                    return (
                      <ReferenceLine
                        key={`th-${index}-${threshold.value}+${threshold.fill}`}
                        y={`${threshold.value}`}
                        stroke={resolveColor(threshold.fill)}
                        strokeDasharray="5 5"
                        strokeWidth={3}
                        yAxisId={getNested(configuration, 'axes.yLeft.show') ? 'left' : 'right'}
                      />
                    );
                  }

                  if (thresholds.thresholdType === THRESHOLD_TYPES.area.value) {
                    return (
                      <ReferenceArea
                        key={`th-${index}-${threshold.min}+${threshold.fill}`}
                        y1={threshold.min}
                        y2={threshold.max}
                        fill={resolveColor(threshold.fill)}
                        yAxisId={getNested(configuration, 'axes.yLeft.show') ? 'left' : 'right'}
                      />
                    );
                  }

                  return null;
                })}
                {showLegend ? (
                  <Legend
                    content={
                      <CustomLegend
                        thresholdsData={thresholds}
                        thresholdItems={sortedThresholds}
                        seriesData={series}
                        hiddenSeries={hiddenSeries}
                        onToggleSeries={handleToggleSeries}
                      />
                    }
                    height={36}
                  />
                ) : null}
              </Chart>
            </ResponsiveContainer>
            {data.length === 0 && (
              <div
                className="no-data"
                style={{
                  marginLeft: getNested(configuration, 'axes.yLeft.show') ? '35px' : 'none',
                  marginRight: getNested(configuration, 'axes.yRight.show') ? '35px' : 'none',
                }}
              >
                No data
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
  (previousProperties, properties) =>
    JSON.stringify(previousProperties) === JSON.stringify(properties)
);

export default GraphVisualization;
